import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react'
import { FiCheckSquare, FiFileText, FiMessageSquare, FiPhone } from 'react-icons/fi'
import { Card, StatusChip, type StatusVariant } from '../../shared'
import { formatSlotTime } from '../scheduler/lobbyScheduleUtils'
import { smsNudgeHref, telHref, type BoardCard, type MeetingPhase } from './dayOfBoardModel'
import type { DayOfLeadDto, ScheduleLegislatorRowDto } from '../../../types/lobbyDay'

const PHASE_CHIP: Record<MeetingPhase, { label: string; variant: StatusVariant }> = {
  upcoming: { label: 'Upcoming', variant: 'neutral' },
  inProgress: { label: 'In progress', variant: 'info' },
  done: { label: 'Done', variant: 'success' },
  reportOwed: { label: 'Report owed', variant: 'warning' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
}

interface DayOfGroupCardProps {
  card: BoardCard
  legislator: ScheduleLegislatorRowDto | undefined
  /** The audited lead read's row for this card's lead assignment, when there is one. */
  leadContact: DayOfLeadDto | undefined
  currentUserId: string | undefined
  onOpen: (meetingId: string) => void
  onPacket: (meetingId: string) => void
  onRecord: (meetingId: string) => void
}

/** One meeting's delegation on the day-of board (DB-1). The body opens the meeting sheet; the
 *  Call / Nudge links and the buttons stop propagation so they never open it as well. */
export function DayOfGroupCard({
  card, legislator, leadContact, currentUserId, onOpen, onPacket, onRecord,
}: DayOfGroupCardProps) {
  const { meeting, phase, strength, lead } = card
  const name = legislator?.fullName ?? 'Unknown legislator'
  const room = meeting.room ?? legislator?.officeRoom ?? null
  const phaseChip = PHASE_CHIP[phase]
  // Phone links only for the CURRENT lead assignment: a lead moved away arrives as a Member on a
  // new assignment id, so a stale leads row never lights up the wrong card (Review Focus #5).
  const contact = lead && leadContact?.assignmentId === lead.assignmentId ? leadContact : undefined
  const callHref = contact ? telHref(contact.phone) : null
  const nudgeHref = contact ? smsNudgeHref(contact, name, meeting.startTime) : null
  const staff = meeting.staff ?? []
  const stop = (e: React.MouseEvent) => e.stopPropagation()

  return (
    <Card
      p={4}
      cursor="pointer"
      _hover={{ bg: 'surface.2' }}
      onClick={() => onOpen(meeting.id)}
      data-testid={`dayof-card-${meeting.id}`}
    >
      <VStack align="stretch" gap={2}>
        <HStack justify="space-between" align="flex-start" gap={2}>
          <VStack align="flex-start" gap={0} minW={0}>
            <Button
              variant="plain"
              p={0}
              h="auto"
              minH="44px"
              fontWeight="semibold"
              fontSize="md"
              textAlign="left"
              whiteSpace="normal"
              justifyContent="flex-start"
              onClick={(e) => { e.stopPropagation(); onOpen(meeting.id) }}
            >
              {name}
            </Button>
            <Text fontSize="sm" color="fg.muted">
              {formatSlotTime(meeting.startTime)} – {formatSlotTime(meeting.endTime)}
            </Text>
            <Text fontSize="sm" color="fg.muted">{room ?? 'No room set'}</Text>
          </VStack>
          <HStack gap={1} flexWrap="wrap" justify="flex-end" flexShrink={0} maxW="50%">
            <StatusChip variant={phaseChip.variant}>{phaseChip.label}</StatusChip>
            {meeting.status === 'DropOff' && <StatusChip variant="warning">Drop-off</StatusChip>}
            {meeting.status === 'Tentative' && <StatusChip variant="neutral">Tentative</StatusChip>}
          </HStack>
        </HStack>

        {(strength.shortHanded || strength.lostConstituent || strength.late > 0 || strength.absent > 0) && (
          <HStack gap={1} flexWrap="wrap">
            {strength.shortHanded && (
              <StatusChip variant="danger">Short-handed ({strength.present} of {strength.assigned})</StatusChip>
            )}
            {strength.lostConstituent && <StatusChip variant="danger">No constituent present</StatusChip>}
            {strength.late > 0 && <StatusChip variant="warning">{strength.late} late</StatusChip>}
            {strength.absent > 0 && <StatusChip variant="danger">{strength.absent} no-show{strength.absent === 1 ? '' : 's'}</StatusChip>}
          </HStack>
        )}

        <HStack justify="space-between" flexWrap="wrap" gap={2}>
          <Text fontSize="sm" color={lead ? 'fg' : 'fg.muted'}>
            {lead ? `Lead: ${lead.fullName}` : 'No team lead'}
          </Text>
          {(callHref || nudgeHref) && (
            <HStack gap={2}>
              {callHref && (
                <Button asChild size="sm" variant="outline" minH="44px">
                  <a href={callHref} onClick={stop}><FiPhone />Call</a>
                </Button>
              )}
              {nudgeHref && (
                <Button asChild size="sm" variant="outline" minH="44px">
                  <a href={nudgeHref} onClick={stop}><FiMessageSquare />Nudge</a>
                </Button>
              )}
            </HStack>
          )}
        </HStack>

        {staff.length > 0 && (
          <Text fontSize="sm" color="fg.muted">
            Staff in this meeting: {staff
              .map((s) => (s.userId === currentUserId ? `${s.displayName} (you)` : s.displayName))
              .join(', ')}
          </Text>
        )}

        <Box>
          <HStack gap={2} flexWrap="wrap">
            <Button
              size="sm"
              variant="outline"
              minH="44px"
              onClick={(e) => { e.stopPropagation(); onPacket(meeting.id) }}
            >
              <FiFileText />Packet
            </Button>
            {(phase === 'reportOwed' || phase === 'done') && (
              <Button
                size="sm"
                variant={phase === 'reportOwed' ? 'solid' : 'outline'}
                colorPalette="brand"
                minH="44px"
                onClick={(e) => { e.stopPropagation(); onRecord(meeting.id) }}
              >
                <FiCheckSquare />Record report
              </Button>
            )}
          </HStack>
        </Box>
      </VStack>
    </Card>
  )
}
